<script>
import Bike from "@/assets/svg/bike.svg";
import BikeCargo from "@/assets/svg/bike_cargo.svg";
import BikeCargoElectric from "@/assets/svg/bike_cargo_electric.svg";
import BikeElectric from "@/assets/svg/bike_electric.svg";
import CarLargeElectric from "@/assets/svg/car_large_electric.svg";
import CarSmallElectric from "@/assets/svg/car_small_electric.svg";
import CarLarge from "@/assets/svg/car_large.svg";
import CarSmall from "@/assets/svg/car_small.svg";
import Trailer from "@/assets/svg/trailer.svg";
import TinyInvoice from "@/components/Invoice/TinyInvoice.vue";
import PricingLoanableTypes from "@/components/Pricing/PricingLoanableTypes.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaymentDialog from "@/components/User/PaymentDialog.vue";
import { capitalize, date } from "@/helpers/filters";
import { put } from "@/requests/server";
import dayjs from "dayjs";
import Vue from "vue";

/**
 * @typedef {{
 *     "created_at": string,
 *     "updated_at": string,
 *     "id": number,
 *     "start_date": string,
 *     "end_date": string,
 *     "type": string,
 *     "reason": string,
 *     "community_id": number,
 *     "loanable_types": [string],
 * }} subscription
 *
 */

export default Vue.extend({
  name: "ProfileCommunitySubscriptions",
  components: {
    IconButton,
    TinyInvoice,
    PricingLoanableTypes,
    PaymentDialog,
    bike_regular: Bike,
    bike_electric: BikeElectric,
    bike_cargo_regular: BikeCargo,
    bike_cargo_electric: BikeCargoElectric,
    car_small: CarSmall,
    car_large: CarLarge,
    car_small_electric: CarSmallElectric,
    car_large_electric: CarLargeElectric,
    trailer: Trailer,
  },
  props: {
    community: {
      type: Object,
      required: true,
    },
    /** @type subscription */
    currentPaidSubscription: {
      type: Object,
      default: null,
    },
    currentGrantedSubscription: {
      type: Object,
      default: null,
    },
    pricingsPerLoanableType: {
      type: Object,
      default: null,
    },
  },
  data() {
    let selectedPricingIds = [];

    if (this.$route.query.loanable_type) {
      if (this.pricingsPerLoanableType[this.$route.query.loanable_type]) {
        selectedPricingIds = this.pricingsPerLoanableType[this.$route.query.loanable_type].map(
          (p) => p.id
        );
      }
    }

    return {
      selectedPricingIds,
      estimation: null,
      estimationLoading: false,
      paying: false,
      isModifying: selectedPricingIds.length > 0,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    unsubscribedLoanableTypes() {
      const unsubscribedLoanableTypes = {};
      for (const loanableType in this.pricingsPerLoanableType) {
        if (!this.subscriptionLoanableTypes.includes(loanableType)) {
          unsubscribedLoanableTypes[loanableType] = this.pricingsPerLoanableType[loanableType];
        }
      }
      return unsubscribedLoanableTypes;
    },
    subscriptionLoanableTypes() {
      const subscriptionLoanableTypes = new Set();

      if (this.currentPaidSubscription) {
        for (const loanableType of this.currentPaidSubscription.loanable_types) {
          subscriptionLoanableTypes.add(loanableType);
        }
      }
      if (this.currentGrantedSubscription) {
        for (const loanableType of this.currentGrantedSubscription.loanable_types) {
          subscriptionLoanableTypes.add(loanableType);
        }
      }
      return [...subscriptionLoanableTypes.values()];
    },
    allPricings() {
      const allPricingIds = new Set();
      const allPricings = [];
      for (const pricings of Object.values(this.pricingsPerLoanableType)) {
        for (const pricing of pricings) {
          if (!allPricingIds.has(pricing.id)) {
            allPricings.push(pricing);
            allPricingIds.add(pricing.id);
          }
        }
      }
      return allPricings;
    },
    selectedLoanableTypes() {
      if (this.selectedPricingIds.length === 0) {
        return [];
      }

      const selectedLoanableTypes = [];
      for (const [loanableType, pricings] of Object.entries(this.pricingsPerLoanableType)) {
        let allSelected = true;
        for (const pricing of pricings) {
          if (!this.selectedPricingIds.includes(pricing.id)) {
            allSelected = false;
            break;
          }
        }
        if (allSelected) {
          selectedLoanableTypes.push(loanableType);
        }
      }

      return selectedLoanableTypes;
    },
    estimationInvoice() {
      if (!this.estimation) {
        return null;
      }
      const invoiceItems = [];
      const reinbursments = [];
      for (const item of this.estimation.items) {
        const selectedPricing = this.allPricings.find((p) => p.id === item.meta.pricing_id);
        const itemData = {
          item_type: item.item_type,
          label: item.label,
          total: -item.total,
          subtext: selectedPricing && item.total < 0 ? selectedPricing.description : null,
        };

        if (item.total < 0) {
          invoiceItems.push(itemData);
        } else {
          reinbursments.push(itemData);
        }
      }

      if (reinbursments.length > 0) {
        invoiceItems.push({
          label: this.$t("views.profile.community_subscriptions.invoice.compensation"),
          items: reinbursments,
        });
      }

      return {
        items: [
          ...invoiceItems,
          {
            label: capitalize(this.$t("invoices.totals.total")),
            class: "invoice-total",
            total: this.estimationTotal,
          },
        ],
      };
    },

    inAYear() {
      return dayjs().add(1, "year");
    },
    invoiceTitle() {
      return `${this.$t("views.profile.community_subscriptions.invoice.title")} ${date(
        this.inAYear
      )}`;
    },
    provisionInvoice() {
      if (!this.estimationTotal) {
        return null;
      }

      let invoice = {
        title: this.$t("views.profile.community_subscriptions.invoice.payment.title"),
        items: [],
      };

      invoice.items.push({
        label: this.$t("views.profile.community_subscriptions.invoice.payment.current_balance"),
        total: this.user.balance,
      });

      if (this.estimationTotal > this.user.balance) {
        invoice.items.push({
          class: "invoice-total",
          label: this.$t("views.profile.community_subscriptions.invoice.payment.total_to_pay"),
          total: this.estimationTotal - this.user.balance,
        });
      } else {
        invoice.items.push({
          class: "invoice-total",
          label: this.$t(
            "views.profile.community_subscriptions.invoice.payment.balance_after_payment"
          ),
          total: this.user.balance - this.estimationTotal,
        });
      }

      return invoice;
    },
    estimationTotal() {
      if (!this.estimation) {
        return null;
      }

      let total = 0;
      for (const item of this.estimation.items) {
        total -= item.total;
      }
      return total;
    },
    loanableTypesForNewSubscription() {
      return [...this.selectedLoanableTypes, ...this.subscriptionLoanableTypes];
    },
  },
  watch: {
    selectedLoanableTypes() {
      if (this.selectedLoanableTypes.length > 0) {
        this.loadEstimation();
      } else {
        this.estimation = null;
      }
    },
  },
  mounted() {
    if (this.selectedPricingIds.length > 0) {
      this.loadEstimation();
    }
  },
  methods: {
    dayjs,
    capitalize,
    async paySubscription() {
      this.paying = true;
      await put(
        "/subscriptions/pay",
        {
          community_id: this.community.id,
          pricing_loanable_types: this.loanableTypesForNewSubscription,
        },
        {
          cleanupCallback: () => (this.paying = false),
          notifications: {
            action: this.$t("views.profile.community_subscriptions.actions.pay"),
            onSuccess: this.$t("views.profile.community_subscriptions.actions.success"),
          },
        }
      );
      // refresh the user's balance
      await this.$store.dispatch("loadUser");
      this.$emit("paid");
      this.cancelEdit();
    },
    toggleSelectedPricings(pricings, loanableType) {
      const selectedPricingIds = new Set(this.selectedPricingIds);
      if (this.selectedLoanableTypes.includes(loanableType)) {
        // deselect all pricings for the current loanable type
        // potential improvement: deselect only the pricings which are least shared with other
        // loanable types.
        for (const pricingId of pricings.map((p) => p.id)) {
          selectedPricingIds.delete(pricingId);
        }

        if (selectedPricingIds.size === 0) {
          this.selectedPricingIds = [];
          return;
        }

        // check if any loanable type would still be selected, if not deselect everything
        for (const pricings of Object.values(this.pricingsPerLoanableType)) {
          let allPricingsSelected = true;
          for (const pricing of pricings) {
            if (!selectedPricingIds.has(pricing.id)) {
              allPricingsSelected = false;
              break;
            }
          }
          if (allPricingsSelected) {
            this.selectedPricingIds = [...selectedPricingIds.values()];
            return;
          }
        }
        this.selectedPricingIds = [];
        return;
      }
      // add all pricings to selected
      for (const pricingId of pricings.map((p) => p.id)) {
        selectedPricingIds.add(pricingId);
      }

      this.selectedPricingIds = [...selectedPricingIds.values()];
    },
    async loadEstimation() {
      this.estimationLoading = true;
      const { data } = await put(
        "/subscriptions/estimate",
        {
          community_id: this.community.id,
          pricing_loanable_types: this.loanableTypesForNewSubscription,
        },
        {
          cleanupCallback: () => (this.estimationLoading = false),
          notifications: {
            action: this.$t("views.profile.community_subscriptions.actions.estimate"),
          },
          requestOptions: {
            cancelId: `subscriptions-estimate-${this.community.id}`,
          },
        }
      );
      this.estimation = data;
    },
    cancelEdit() {
      this.isModifying = false;
      this.selectedPricingIds = [];
      if (this.$route.query.community_id || this.$route.query.loanable_type) {
        this.$router.replace({ query: {} });
      }
    },
  },
});
</script>

<template>
  <div class="community-subscription">
    <b-alert v-if="currentGrantedSubscription" class="mb-3" variant="success" show>
      {{ currentGrantedSubscription.granted_by_user.full_name }}
      {{ $t("views.profile.community_subscriptions.granted.message") }}
      {{ currentGrantedSubscription.end_date | date }}
      <span v-if="currentGrantedSubscription.reason">
        {{ $t("views.profile.community_subscriptions.granted.reason") }} "{{
          currentGrantedSubscription.reason
        }}"</span
      >. {{ $t("views.profile.community_subscriptions.granted.no_payment") }}
    </b-alert>
    <div v-if="currentPaidSubscription && !isModifying">
      <p>
        {{ $t("views.profile.community_subscriptions.paid.paid_on") }}
        {{ currentPaidSubscription.start_date | date }}.<br />
        {{ $t("views.profile.community_subscriptions.paid.active_until") }}
        {{ currentPaidSubscription.end_date | date }}.
      </p>

      <div class="loanable-types-selection">
        <div
          v-for="loanableType in subscriptionLoanableTypes"
          :key="loanableType"
          class="loanable-type paid"
        >
          <component :is="loanableType" class="mb-1 loanable-svg" />
          <div>{{ $t(`pricings.loanableTypes.${loanableType}`) | capitalize }}</div>
        </div>

        <icon-button
          v-if="unsubscribedLoanableTypes && Object.keys(unsubscribedLoanableTypes).length > 0"
          variant="ghost-secondary"
          icon="plus-circle"
          @click="isModifying = true"
        >
          {{ $t("forms.add") | capitalize }}
        </icon-button>
      </div>
    </div>
    <div v-if="pricingsPerLoanableType && (isModifying || !currentPaidSubscription)">
      <h3>
        {{ $t("views.profile.community_subscriptions.selection.title") }}
        <span class="font-weight-bold text-primary">{{ community.name }}</span
        >?
      </h3>

      <p>
        {{ $t("views.profile.community_subscriptions.selection.description") }}
      </p>
      <div class="loanable-types-selection">
        <icon-button
          v-for="loanableType in subscriptionLoanableTypes"
          :key="loanableType"
          variant="ghost-secondary"
          pressed
          disabled
          class="loanable-type selection-btn"
        >
          <div>
            <component :is="loanableType" class="mb-1 loanable-svg" />
            <div>{{ $t(`pricings.loanableTypes.${loanableType}`) | capitalize }}</div>
          </div>
          <b-icon class="implicitly-selected-check" icon="check-circle-fill" />
        </icon-button>
        <icon-button
          v-for="(pricings, loanableType) in unsubscribedLoanableTypes"
          :key="loanableType"
          :variant="selectedLoanableTypes.includes(loanableType) ? 'success' : 'ghost-secondary'"
          :pressed="selectedLoanableTypes.includes(loanableType)"
          :disabled="paying"
          class="loanable-type selection-btn"
          @click="() => toggleSelectedPricings(pricings, loanableType)"
        >
          <div>
            <component :is="loanableType" class="mb-1 loanable-svg" />
            <div>{{ $t(`pricings.loanableTypes.${loanableType}`) | capitalize }}</div>
          </div>
          <b-icon
            v-if="selectedLoanableTypes.includes(loanableType)"
            class="implicitly-selected-check"
            icon="check-circle-fill"
          />
          <b-icon v-else class="implicitly-selected-check" icon="circle" />
        </icon-button>
      </div>
      <div v-if="estimation || estimationLoading" class="mt-3">
        <tiny-invoice class="invoice" :invoice="estimationInvoice">
          <template #title>
            <h3>
              {{ invoiceTitle }}
              <layout-loading v-if="estimationLoading" with-text />
            </h3>
          </template>
        </tiny-invoice>
        <tiny-invoice v-if="provisionInvoice" class="invoice mt-2" :invoice="provisionInvoice" />
        <payment-dialog
          v-if="estimationTotal"
          class="mt-3"
          :price="estimationTotal"
          :user="user"
          minimum-only
          action-name="payer"
          :loading="paying"
          :disabled="estimationLoading"
          :show-cancel="!!currentPaidSubscription"
          @complete="paySubscription"
          @cancel="cancelEdit"
        >
          {{ $t("views.profile.community_subscriptions.actions.pay") }}
        </payment-dialog>
      </div>
      <div v-if="!estimationTotal && currentPaidSubscription" class="text-center mt-3">
        <icon-button role="cancel" @click="cancelEdit">
          {{ $t("forms.cancel") | capitalize }}
        </icon-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.community-subscription {
  h2 {
    font-size: $h3-font-size;
    line-height: $h3-line-height;
  }

  h3 {
    font-weight: 400;
    font-size: $h4-font-size;
    line-height: $h4-line-height;
  }

  .loanable-types-selection {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));

    .loanable-svg {
      width: 2.5rem;
    }

    .paid {
      border: 1px solid transparent;
      padding: 0.375rem 0.75rem;
      border-radius: 0.25rem;
      background-color: $success;
      color: white;
    }

    .paid svg path {
      fill: white;
    }

    .loanable-type {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      text-align: center;
      font-size: 0.8rem;
      height: 4.5rem;

      &.btn {
        .loanable-svg path {
          fill: currentColor;
        }
        &.active {
          background-color: $success;
          color: white;
          .loanable-svg path {
            fill: white;
          }
        }
      }

      &.selection-btn {
        position: relative;
        .implicitly-selected-check {
          position: absolute;
          background: white;
          border-radius: 50%;
          border: 1px solid white;
          color: $success;
          top: 0.25rem;
          right: 0.25rem;
        }
      }
    }
  }
}
</style>
